/**
 * CrmModuleGuard - Proteção de rota para o módulo CRM
 * 
 * Verifica se o tenant atual possui o módulo CRM habilitado (crm_enabled).
 * Se não possuir, registra tentativa de bypass e redireciona para /tenant.
 * 
 * Deve envolver TODAS as rotas /tenant/crm/* no App.tsx,
 * ficando entre TenantProtectedRoute e PermissionGuard.
 * 
 * @version 1.0.0
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para auditoria de segurança do módulo CRM
const crmLogger = createLogger('CrmModuleGuard');

interface CrmModuleGuardProps {
  /** Conteúdo a ser renderizado se o módulo CRM estiver habilitado */
  children: ReactNode;
}

/**
 * Registra tentativa de acesso ao CRM sem módulo habilitado
 * na tabela security_bypass_attempts para auditoria
 */
const logCrmBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  route: string
): Promise<void> => {
  try {
    if (!userId) return;

    // Buscar full_name para incluir no details (conforme padrão de segurança)
    let fullName: string | null = null;
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      fullName = profileData?.full_name || null;
    } catch {
      // Não bloquear se falhar busca do perfil
    }

    const { error } = await supabase
      .from('security_bypass_attempts')
      .insert({
        user_id: userId,
        attempted_tenant_id: tenantId || null,
        function_name: 'CrmModuleGuard',
        action: 'crm_module_access_denied',
        attempted_value: route,
        user_agent: navigator.userAgent,
        details: {
          route,
          full_name: fullName,
          reason: 'tenant_crm_not_enabled',
          timestamp: new Date().toISOString(),
        },
      });

    if (error) {
      crmLogger.error('Falha ao registrar tentativa de bypass CRM', { error: error.message });
    } else {
      crmLogger.warn('Tentativa de acesso ao CRM sem módulo habilitado registrada', {
        route,
        tenantId,
      });
    }
  } catch (err) {
    crmLogger.error('Erro ao registrar bypass attempt CRM', { err });
  }
};

/**
 * Componente guard que bloqueia acesso às rotas CRM
 * quando o tenant atual não possui crm_enabled = true
 */
export function CrmModuleGuard({ children }: CrmModuleGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();

  // Ref para evitar múltiplos logs da mesma tentativa
  const hasLoggedBypass = useRef(false);

  // Verificar se o módulo CRM está habilitado no tenant atual
  const crmEnabled = currentTenant?.crm_enabled ?? false;

  // Registrar tentativa de bypass quando módulo não está habilitado
  useEffect(() => {
    if (!crmEnabled && !hasLoggedBypass.current && currentTenant) {
      hasLoggedBypass.current = true;
      logCrmBypassAttempt(user?.id, currentTenant?.id, location.pathname);
    }
  }, [crmEnabled, user?.id, currentTenant, location.pathname]);

  // Se CRM não habilitado, redirecionar para dashboard principal
  if (!crmEnabled) {
    crmLogger.warn('Acesso ao CRM bloqueado: módulo não habilitado para o tenant', {
      tenantId: currentTenant?.id,
      route: location.pathname,
    });
    return <Navigate to="/tenant" replace />;
  }

  // Módulo CRM habilitado - renderizar conteúdo
  return <>{children}</>;
}
